import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'
import type { ProductReviewFormState } from './ProductReviewFormState'

import React, { useState, useCallback, useEffect } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { useFetchSingleProductReview } from '@@partner:graphql/query/product/productReviews/singleProductReview.gql'
import { useUpdateProductReview } from '@@partner:graphql/mutation/catalog/updateProductReview'

import { Link, generatePath } from 'react-router-dom'
import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../helpers/notification'
import {
  makeProductReviewFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './ProductReviewFormState'

import { Button, Form, Grid, Panel, Row, Col, Rate } from 'rsuite'
import FormSelect from '@components/common/form/FormSelect'
import FormTextareaControl from '@components/common/form/FormTextareaControl'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import {
  ROUTE_CUSTOMERS_VIEW_CUSTOMER,
  ROUTE_CATALOG_PRODUCT_EDIT
} from '@@partner:config/routes'
import {
  PRODUCT_REVIEW_STATUS_REJECTED,
  PRODUCT_REVIEW_STATUS_AWAITING_MODERATION,
  PRODUCT_REVIEW_STATUS_PASSED_MODERATION
} from '@@partner:config/productReview-status-codes'
import { NO_PHOTO_IMAGE } from '@@partner:config/image'

export interface ProductReviewFormProps extends CrudFormProps {
  productReviewId?: GQL_ID
  brandSlug: string
}

export default function ProductReviewForm ({
  productReviewId,
  brandSlug,
  onSaved
}: ProductReviewFormProps) {
  const [ formError, setFormError ] = useState({})
  const { formState, setFormState, setFormRef } =
    useValidatableForm<ProductReviewFormState>(makeProductReviewFormState)

  const { data, loading, error } = useFetchSingleProductReview({
    id: productReviewId
  })
  const [ updateProductReview, { loading: loadingUpdateProductReview } ] =
    useUpdateProductReview()

  const productPreviewUrl =
    data?.productReview?.product?.preview?.url ?? NO_PHOTO_IMAGE

  const product = data?.productReview?.product
  const customer = data?.productReview?.customer

  const productReviewStatusLabels = [
    { label: 'Отклонен', value: PRODUCT_REVIEW_STATUS_REJECTED },
    {
      label: 'Ожидает модерации',
      value: PRODUCT_REVIEW_STATUS_AWAITING_MODERATION
    },
    {
      label: 'Прошел модерацию',
      value: PRODUCT_REVIEW_STATUS_PASSED_MODERATION
    }
  ]

  useEffect(() => {
    if (!loading && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.productReview))
      )
    }
  }, [ data, loading, setFormState ])

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        updateProductReview({
          variables: {
            newData: mapStateToApiVariable(formState),
            upsert: formState.id ? false : true
          }
        })
          .then(({ data }) => {
            notifyOk('Отзыв сохранён')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.productReview)
            )

            onSaved?.()
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ updateProductReview, formState, setFormState, onSaved ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить баннер #${productReviewId} для редактирования`}
        error={error}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      onCheck={handleFormError}
      ref={setFormRef}
      fluid
    >
      <Panel className="mb-4">
        <Grid fluid>
          <Row>
            <Col md={12}>
              <Panel header={`Отзыв № ${productReviewId}`} shaded>
                <div className="flex flex-col">
                  <span>Рейтинг</span>
                  <Rate
                    value={data?.productReview?.rating}
                    readOnly
                    size="xs"
                    className="mb-6"
                  />
                </div>
                <Form.Group>
                  <Form.ControlLabel>Статус</Form.ControlLabel>
                  <FormSelect name="status" data={productReviewStatusLabels} />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Комментарий</Form.ControlLabel>
                  <FormTextareaControl
                    placeholder="Комментарий"
                    name="comment"
                    rows={5}
                  />
                </Form.Group>
              </Panel>
            </Col>
            <Col md={6}>
              {product && (
                <Panel header="Товар" shaded>
                  <Button
                    block
                    as={Link}
                    to={generatePath(ROUTE_CATALOG_PRODUCT_EDIT, {
                      brandSlug,
                      productId: product.id as GQL_ID
                    })}
                    className="flex items-center flex-col whitespace-pre-line"
                  >
                    <Row>
                      <Col>
                        <img src={productPreviewUrl} alt={product?.name} />
                      </Col>
                    </Row>
                    <Row>
                      <Col>{product?.name}</Col>
                    </Row>
                  </Button>
                </Panel>
              )}
            </Col>
            <Col md={6}>
              {customer && (
                <Panel header="Покупатель" shaded>
                  <Button
                    block
                    as={Link}
                    to={generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, {
                      brandSlug,
                      customerId: customer.id
                    })}
                  >
                    <Row>
                      <Col>
                        {customer?.first_name} {customer?.last_name}
                      </Col>
                    </Row>
                  </Button>
                </Panel>
              )}
            </Col>
          </Row>
        </Grid>
      </Panel>
      <SaveButton
        loading={loading || loadingUpdateProductReview}
        disabled={loading || loadingUpdateProductReview}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
